"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SaveCancelBlock = void 0;
const react_1 = __importDefault(require("react"));
const immer_1 = __importDefault(require("immer"));
const blocks_1 = require("../blocks");
const localization_1 = require("../localization");
const propertyEditors_1 = require("../propertyEditors");
const expressions_1 = require("@mwater/expressions");
const ListEditorComponent_1 = require("@mwater/react-library/lib/ListEditorComponent");
const __1 = require("../..");
const expressions_ui_1 = require("@mwater/expressions-ui");
const bootstrap_1 = require("@mwater/react-library/lib/bootstrap");
const SaveCancelInstance_1 = require("./SaveCancelInstance");
/** Block that has a save/cancel button pair at bottom. Changes are only sent to the database if save is clicked.
 * When either is clicked, the page is closed. Has optional delete button too.
 *
 * After a delete, closes the page and any parent pages that reference the deleted row.
 */
class SaveCancelBlock extends blocks_1.Block {
    getChildren(contextVars) {
        if (this.blockDef.child) {
            const contextVar = this.createAddContextVar();
            return [
                { blockDef: this.blockDef.child, contextVars: contextVar ? contextVars.concat([contextVar]) : contextVars }
            ];
        }
        return [];
    }
    validate(ctx) {
        if (!this.blockDef.saveLabel) {
            return "Save label required";
        }
        if (!this.blockDef.cancelLabel) {
            return "Cancel label required";
        }
        if (!this.blockDef.confirmDiscardMessage) {
            return "Confirm discard message required";
        }
        if (this.blockDef.deleteContextVarId) {
            if (!this.blockDef.deleteLabel) {
                return "Delete label required";
            }
            const deleteCV = ctx.contextVars.find((cv) => cv.id == this.blockDef.deleteContextVarId);
            if (!deleteCV) {
                return "Delete context variable not found";
            }
            if (deleteCV.type !== "row") {
                return "Delete context variable wrong type";
            }
        }
        // Check extras
        if (this.blockDef.deleteContextVarId && this.blockDef.extraDeleteContextVarIds) {
            for (const cvId of this.blockDef.extraDeleteContextVarIds) {
                const deleteCV = ctx.contextVars.find((cv) => cv.id == cvId);
                if (!deleteCV) {
                    return "Delete context variable not found";
                }
                if (deleteCV.type !== "row") {
                    return "Delete context variable wrong type";
                }
            }
        }
        if (this.blockDef.deleteCondition) {
            const error = (0, __1.validateContextVarExpr)({
                schema: ctx.schema,
                contextVars: ctx.contextVars,
                contextVarId: this.blockDef.deleteCondition.contextVarId,
                expr: this.blockDef.deleteCondition.expr,
                aggrStatuses: ["individual", "literal"],
                types: ["boolean"]
            });
            if (error) {
                return error;
            }
        }
        // Validate add options
        if (this.blockDef.addTable) {
            // Check that table is present
            if (!ctx.schema.getTable(this.blockDef.addTable)) {
                return "Invalid add table";
            }
            // Check that existing context variable from same table
            if (this.blockDef.addTable && this.blockDef.addExistingContextVarId) {
                const cv = ctx.contextVars.find((cv) => cv.id == this.blockDef.addExistingContextVarId);
                if (!cv) {
                    return "Existing context variable not found";
                }
                if (cv.table != this.blockDef.addTable) {
                    return "Existing context variable from wrong table";
                }
            }
            // Check each column value
            for (const columnId of Object.keys(this.blockDef.addColumnValues || {})) {
                const error = this.validateColumnValue(ctx, columnId);
                if (error) {
                    return error;
                }
            }
        }
        if (this.blockDef.postAddAction) {
            const action = ctx.actionLibrary.createAction(this.blockDef.postAddAction);
            // Create row context variable
            const addCV = this.getAddContextVar(ctx);
            const error = action.validate({ ...ctx, contextVars: ctx.contextVars.concat(addCV) });
            if (error) {
                return error;
            }
        }
        return null;
    }
    validateColumnValue(ctx, columnId) {
        // Check that column exists
        const column = ctx.schema.getColumn(this.blockDef.addTable, columnId);
        if (!column) {
            return "Column not found";
        }
        const exprValidator = new expressions_1.ExprValidator(ctx.schema.addVariables((0, blocks_1.createExprVariables)(ctx.contextVars), {}));
        const exprUtils = new expressions_1.ExprUtils(ctx.schema.addVariables((0, blocks_1.createExprVariables)(ctx.contextVars), {}));
        // Check context var
        const contextVarExpr = this.blockDef.addColumnValues[columnId];
        let contextVar;
        if (contextVarExpr.contextVarId) {
            contextVar = ctx.contextVars.find((cv) => cv.id === contextVarExpr.contextVarId);
            if (!contextVar || !contextVar.table) {
                return "Context variable not found";
            }
        }
        else {
            contextVar = undefined;
            // Must be literal
            const aggrStatus = exprUtils.getExprAggrStatus(contextVarExpr.expr);
            if (aggrStatus && aggrStatus !== "literal") {
                return "Literal value required";
            }
        }
        // Override for special case of allowing to set joins
        const idTable = column.type == "join" ? column.join.toTable : column.idTable;
        const type = column.type == "join" ? "id" : column.type;
        // Validate expr
        const error = exprValidator.validateExpr(contextVarExpr.expr, {
            table: contextVar ? contextVar.table : undefined,
            types: [type],
            idTable: idTable,
            aggrStatuses: contextVar && contextVar.type == "rowset" ? ["aggregate", "literal"] : ["individual", "literal"]
        });
        if (error) {
            return error;
        }
        return null;
    }
    /** Create a context variable for the added row if not reusing existing */
    createAddContextVar() {
        // Don't create new context variable if reusing existing
        if (this.blockDef.addTable && !this.blockDef.addExistingContextVarId) {
            return { type: "row", id: this.blockDef.id, name: this.blockDef.addContextVarName || "Added row", table: this.blockDef.addTable };
        }
        return null;
    }
    /** Get context variable for added row if not reusing existing */
    getAddContextVar(ctx) {
        return this.createAddContextVar() || ctx.contextVars.find((cv) => cv.id == this.blockDef.addExistingContextVarId);
    }
    /** Get any context variables expressions that this block needs (not including child blocks) */
    getContextVarExprs(contextVar, ctx) {
        const exprs = [];
        if (this.blockDef.deleteCondition != null &&
            contextVar.id === this.blockDef.deleteCondition.contextVarId &&
            this.blockDef.deleteCondition.expr) {
            exprs.push(this.blockDef.deleteCondition.expr);
        }
        // Get ones for the specified context var
        if (this.blockDef.addTable && this.blockDef.addColumnValues) {
            for (const columnId of Object.keys(this.blockDef.addColumnValues)) {
                const cve = this.blockDef.addColumnValues[columnId];
                if (cve.contextVarId === contextVar.id) {
                    exprs.push(cve.expr);
                }
            }
        }
        return exprs;
    }
    processChildren(action) {
        const child = action(this.blockDef.child);
        return (0, immer_1.default)(this.blockDef, (draft) => {
            draft.child = child;
        });
    }
    renderDesign(props) {
        const handleAdd = (addedBlockDef) => {
            props.store.alterBlock(this.id, (0, immer_1.default)((b) => {
                b.child = addedBlockDef;
                return b;
            }), addedBlockDef.id);
        };
        const saveLabelText = (0, localization_1.localize)(this.blockDef.saveLabel, props.locale);
        const cancelLabelText = (0, localization_1.localize)(this.blockDef.cancelLabel, props.locale);
        const deleteLabelText = (0, localization_1.localize)(this.blockDef.deleteLabel, props.locale);
        // Create props for child
        const addContextVar = this.createAddContextVar();
        let contentProps = props;
        // Add context variable if knowable
        if (addContextVar) {
            contentProps = { ...contentProps, contextVars: [...props.contextVars, addContextVar] };
        }
        return (react_1.default.createElement("div", null,
            props.renderChildBlock(contentProps, this.blockDef.child, handleAdd),
            react_1.default.createElement("div", { className: "save-cancel-footer" },
                this.blockDef.deleteContextVarId ? (react_1.default.createElement("button", { type: "button", className: "btn btn-outline-danger", style: { float: "left" } },
                    react_1.default.createElement("i", { className: "fa fa-remove" }),
                    " ",
                    deleteLabelText)) : null,
                react_1.default.createElement("button", { type: "button", className: "btn btn-primary" }, saveLabelText),
                "\u00A0",
                react_1.default.createElement("button", { type: "button", className: "btn btn-secondary" }, cancelLabelText))));
    }
    /** Special case as the inner block will have a virtual database and its own expression evaluator */
    getSubtreeContextVarExprs(contextVar, ctx) {
        return this.getContextVarExprs(contextVar, ctx);
    }
    renderInstance(props) {
        let addContextVar = this.getAddContextVar(props);
        // If context variable already has a value, add is not required
        if (addContextVar && props.contextVarValues[addContextVar.id]) {
            addContextVar = null;
        }
        return react_1.default.createElement(SaveCancelInstance_1.SaveCancelInstance, { instanceCtx: props, blockDef: this.blockDef, addContextVar: addContextVar });
    }
    renderEditor(props) {
        // Determine if can upgrade from older pattern of save/cancel with addBlock within
        const canUpgrade = !this.blockDef.addTable && this.blockDef.child && this.blockDef.child.type == "addRow";
        const upgradeToNewBlock = () => {
            const child = this.blockDef.child;
            const newBlock = (0, immer_1.default)(this.blockDef, (draft) => {
                draft.addTable = child.table;
                draft.addExistingContextVarId = child.existingContextVarId;
                draft.addContextVarName = child.name;
                draft.addColumnValues = child.columnValues;
                // Switch out id of child content
                draft.child = JSON.parse(JSON.stringify(child.content).replaceAll(child.id, this.blockDef.id));
            });
            props.store.replaceBlock(newBlock);
        };
        return (react_1.default.createElement("div", null,
            react_1.default.createElement(propertyEditors_1.LabeledProperty, { label: "Save Label" },
                react_1.default.createElement(propertyEditors_1.PropertyEditor, { obj: this.blockDef, onChange: props.store.replaceBlock, property: "saveLabel" }, (value, onChange) => (react_1.default.createElement(propertyEditors_1.LocalizedTextPropertyEditor, { value: value, onChange: onChange, locale: props.locale })))),
            react_1.default.createElement(propertyEditors_1.LabeledProperty, { label: "Cancel Label" },
                react_1.default.createElement(propertyEditors_1.PropertyEditor, { obj: this.blockDef, onChange: props.store.replaceBlock, property: "cancelLabel" }, (value, onChange) => (react_1.default.createElement(propertyEditors_1.LocalizedTextPropertyEditor, { value: value, onChange: onChange, locale: props.locale })))),
            react_1.default.createElement(propertyEditors_1.LabeledProperty, { label: "Confirm Discard Message" },
                react_1.default.createElement(propertyEditors_1.PropertyEditor, { obj: this.blockDef, onChange: props.store.replaceBlock, property: "confirmDiscardMessage" }, (value, onChange) => (react_1.default.createElement(propertyEditors_1.LocalizedTextPropertyEditor, { value: value, onChange: onChange, locale: props.locale })))),
            react_1.default.createElement(propertyEditors_1.LabeledProperty, { label: "Optional Delete Target" },
                react_1.default.createElement(propertyEditors_1.PropertyEditor, { obj: this.blockDef, onChange: props.store.replaceBlock, property: "deleteContextVarId" }, (value, onChange) => (react_1.default.createElement(propertyEditors_1.ContextVarPropertyEditor, { value: value, onChange: onChange, contextVars: props.contextVars, types: ["row"] })))),
            this.blockDef.deleteContextVarId ? (react_1.default.createElement(propertyEditors_1.LabeledProperty, { label: "Delete Label" },
                react_1.default.createElement(propertyEditors_1.PropertyEditor, { obj: this.blockDef, onChange: props.store.replaceBlock, property: "deleteLabel" }, (value, onChange) => (react_1.default.createElement(propertyEditors_1.LocalizedTextPropertyEditor, { value: value, onChange: onChange, locale: props.locale }))))) : null,
            this.blockDef.deleteContextVarId ? (react_1.default.createElement(propertyEditors_1.LabeledProperty, { label: "Optional Confirm Delete Message" },
                react_1.default.createElement(propertyEditors_1.PropertyEditor, { obj: this.blockDef, onChange: props.store.replaceBlock, property: "confirmDeleteMessage" }, (value, onChange) => (react_1.default.createElement(propertyEditors_1.LocalizedTextPropertyEditor, { value: value, onChange: onChange, locale: props.locale }))))) : null,
            this.blockDef.deleteContextVarId ? (react_1.default.createElement(propertyEditors_1.LabeledProperty, { label: "Delete condition", hint: "optional expression that must be true to show delete button" },
                react_1.default.createElement(propertyEditors_1.PropertyEditor, { obj: this.blockDef, onChange: props.store.replaceBlock, property: "deleteCondition" }, (value, onChange) => (react_1.default.createElement(__1.ContextVarExprPropertyEditor, { schema: props.schema, dataSource: props.dataSource, contextVars: props.contextVars, contextVarExpr: value, onChange: onChange, types: ["boolean"] }))))) : null,
            this.blockDef.deleteContextVarId ? (react_1.default.createElement(propertyEditors_1.LabeledProperty, { label: "Optional Additional Delete Targets" },
                react_1.default.createElement(propertyEditors_1.PropertyEditor, { obj: this.blockDef, onChange: props.store.replaceBlock, property: "extraDeleteContextVarIds" }, (value, onChange) => {
                    function renderItem(item, index, onItemChange) {
                        return (react_1.default.createElement(propertyEditors_1.ContextVarPropertyEditor, { value: item, onChange: onItemChange, contextVars: props.contextVars, types: ["row"] }));
                    }
                    return (react_1.default.createElement(ListEditorComponent_1.ListEditorComponent, { items: value || [], onItemsChange: onChange, renderItem: renderItem, addLabel: "Add", createNew: () => null }));
                }))) : null,
            react_1.default.createElement(bootstrap_1.CollapsibleSection, { label: "Add Row", hint: "Optionally add a row on save", initiallyOpen: this.blockDef.addTable != null },
                react_1.default.createElement(propertyEditors_1.LabeledProperty, { label: "Add Table", hint: "Table to add row to" },
                    react_1.default.createElement(propertyEditors_1.PropertyEditor, { obj: this.blockDef, onChange: props.store.replaceBlock, property: "addTable" }, (value, onChange) => (react_1.default.createElement(expressions_ui_1.TableSelectComponent, { schema: props.schema, value: value, onChange: (table) => onChange(table ?? undefined) })))),
                this.blockDef.addTable ? (react_1.default.createElement(propertyEditors_1.LabeledProperty, { label: "Mode" },
                    react_1.default.createElement(propertyEditors_1.PropertyEditor, { obj: this.blockDef, onChange: props.store.replaceBlock, property: "addExistingContextVarId" }, (value, onChange) => (react_1.default.createElement("div", null,
                        react_1.default.createElement(bootstrap_1.Radio, { key: "null", radioValue: null, value: value || null, onChange: onChange }, "Always add new row"),
                        props.contextVars
                            .filter((cv) => cv.table == this.blockDef.addTable && cv.type == "row")
                            .map((cv) => (react_1.default.createElement(bootstrap_1.Radio, { key: cv.id, radioValue: cv.id, value: value, onChange: onChange },
                            "Use ",
                            react_1.default.createElement("i", null, cv.name),
                            " if it has a value")))))))) : null,
                this.blockDef.addTable && !this.blockDef.addExistingContextVarId ? (react_1.default.createElement(propertyEditors_1.LabeledProperty, { label: "Variable Name" },
                    react_1.default.createElement(propertyEditors_1.PropertyEditor, { obj: this.blockDef, onChange: props.store.replaceBlock, property: "addContextVarName" }, (value, onChange) => react_1.default.createElement(bootstrap_1.TextInput, { value: value || null, onChange: onChange, placeholder: "Added row" })))) : null,
                this.blockDef.addTable ? (react_1.default.createElement(propertyEditors_1.LabeledProperty, { label: "Column Values" },
                    react_1.default.createElement(propertyEditors_1.PropertyEditor, { obj: this.blockDef, onChange: props.store.replaceBlock, property: "addColumnValues" }, (value, onChange) => (react_1.default.createElement(__1.ColumnValuesEditor, { value: value || {}, onChange: onChange, schema: props.schema, dataSource: props.dataSource, table: this.blockDef.addTable, contextVars: props.contextVars, locale: props.locale }))))) : null,
                this.blockDef.addTable ? (react_1.default.createElement(propertyEditors_1.LabeledProperty, { label: "Post Add Action", hint: "Action to perform after row is added and saved and page closed" },
                    react_1.default.createElement(propertyEditors_1.PropertyEditor, { obj: this.blockDef, onChange: props.store.replaceBlock, property: "postAddAction" }, (value, onChange) => (react_1.default.createElement(propertyEditors_1.ActionDefEditor, { value: value, onChange: onChange, designCtx: { ...props, contextVars: !this.blockDef.addExistingContextVarId ? props.contextVars.concat(this.createAddContextVar()) : props.contextVars } }))))) : null),
            canUpgrade ? (react_1.default.createElement("div", null,
                react_1.default.createElement("button", { type: "button", className: "btn btn-primary", onClick: () => upgradeToNewBlock() }, "Upgrade To Single Block"),
                react_1.default.createElement("p", null, "This block is using an older pattern of save/cancel with an add block within."),
                react_1.default.createElement("p", null, "Upgrade to the new save/cancel block that adds the row directly."))) : null));
    }
}
exports.SaveCancelBlock = SaveCancelBlock;
